import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import TextBlock from '../../../components/text-block/text-block.component';
import Loader from '../../../components/loader/loader.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsDealTemplateModal from './settings-deal-template-modal/settings-deal-template-modal.component';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import SettingsDealTemplateListValidator from './settings-deal-template-list.validator';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import {
  getDealTemplateListData, saveDealTemplateListData, deleteDealTemplate,
} from './settings-deal-template-list.action';
import { getSettingsData } from '../settings.action';
import { resetFormErrors } from '../../app/app.action';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  DEAL_TEMPLATE_LIST_VIEW, DEAL_TEMPLATE_CREATE, DEAL_TEMPLATE_DELETE,
} from '../../../constants/entity/permissions';
import { ROUTE_DEAL_TEMPLATE } from '../../../constants/system/routes';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { FORM_ID_SETTINGS_DEAL_TEMPLATE_LIST } from '../../../constants/system/form-ids';
import { DATA_DEAL_TEMPLATE_ID } from '../../../constants/system/data-attributes';
import { TAB_NAME_DEAL_TEMPLATES } from '../../../constants/system/tab-names';

const SettingsDealTemplateList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    dealTemplateListData,
    dealTemplateTypeList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
  } = formProps;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const pageTitle: string = 'Settings: Deal Templates';
  const createButtonTitle: string | null = 'New Template';
  const deleteConfirmModalTitle: string = 'Delete Deal Template';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this deal template?';
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const [isDealTemplateModalOpen, setIsDealTemplateModalOpen]: [boolean, Function] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [dealTemplateActiveId, setDealTemplateActiveId]: [number | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');

  const dealTemplateListViewPermissions: string[] = useMemo((): string[] => [DEAL_TEMPLATE_LIST_VIEW], []);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const isFormDataPristine: boolean = isFormPristine;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setDealTemplateActiveId(null);
  }, []);

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealTemplate: DealTemplate,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && dealTemplate.id) {
      navigate({
        pathname: `${ROUTE_DEAL_TEMPLATE}/${dealTemplate.id}`,
      });
    }
  }, []);

  const openModal: Function = useCallback((): void => {
    setIsDealTemplateModalOpen(true);
  }, []);

  const closeModal: Function = useCallback((): void => {
    setIsDealTemplateModalOpen(false);
  }, []);

  const onCreateButtonClick: Function = useCallback((): void => {
    openModal();
  }, []);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const formData: FormValueItem = {
      [formKeyNew]: {
        ...values.dealTemplateListData[formKeyNew],
      },
    };

    const dealTemplateList: DealTemplateInput[] = [];
    Object.keys(formData).forEach((dealTemplateIndex: string): void => {
      const dealTemplateItem: DealTemplateInput = {
        ...formData[dealTemplateIndex],
      };

      dealTemplateItem.initialData = undefined;
      dealTemplateItem.hiddenFields = undefined;
      dealTemplateItem.customSections = undefined;
      dealTemplateItem.customListTypes = undefined;

      dealTemplateList.push(dealTemplateItem);
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealTemplateListData({
        dealTemplateListForm: {
          dealTemplateList,
        },
      }, (response: ActionResponse): void => {
        closeModal();

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, formKeyNew]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, formValues, withFormErrors]);

  const openDealTemplateDeleteConfirmModal: Function = useCallback((dealTemplateId: number): void => {
    setIsDeleteConfirmModalOpen(true);
    setDealTemplateActiveId(dealTemplateId);
  }, []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
    dispatch(getDealTemplateListData());
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_TEMPLATE_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTemplateId: number): void => {
        openDealTemplateDeleteConfirmModal(dealTemplateId);
      },
    },
  ], []);

  const tableDealTemplateListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'title',
      title: 'Title',
      component: (dealTemplate: DealTemplate): ReactElement => (
        <TableCell>
          {dealTemplate.title}
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'type',
      title: 'Type',
      component: (dealTemplate: DealTemplate): ReactElement => (
        <TableCell>
          {dealTemplate.typeText}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'description',
      title: 'Description',
      component: (dealTemplate: DealTemplate): ReactElement => (
        <TableCell>
          {dealTemplate.description}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'actions',
      title: 'Actions',
      isHeaderNameHidden: true,
      component: (dealTemplate: DealTemplate): ReactElement => (
        <TableCell isActions isTextRight>
          {
            !dealTemplate.isSystem && (
              <ActionButton
                withPopup
                withNoPadding
                onClick={openActionMenu}
                {...{
                  [DATA_DEAL_TEMPLATE_ID]: dealTemplate.id,
                }}
              >
                <MoreIcon />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onDealTemplateDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealTemplateActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealTemplate({
        dealTemplateId: dealTemplateActiveId,
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealTemplateActiveId]);

  const closeDealTemplateDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setDealTemplateActiveId(null);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    dispatch(getSettingsData());
    dispatch(getDealTemplateListData());
  }, [queryTab]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  return (
    <CheckAccess permissions={dealTemplateListViewPermissions}>
      <Form id={FORM_ID_SETTINGS_DEAL_TEMPLATE_LIST} onChange={onFormChange}>
        <PullToRefresh onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_DEAL_TEMPLATES}
            currentUser={currentUser}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && dealTemplateListData && !!dealTemplateListData.length && (
              <Fragment>
                <Controls
                  withNoEditMode
                  createButtonTitle={createButtonTitle}
                  createPermission={DEAL_TEMPLATE_CREATE}
                  onCreateButtonClick={onCreateButtonClick}
                />
                {
                  dealTemplateListData && !!dealTemplateListData.length ? (
                    <Card>
                      {
                        !isSmallScreen && (
                          <ContentListContainerOffset withTabs>
                            <Table withStickyHeader>
                              <TableHead isHeader withFixedCellsSize>
                                <TableRow>
                                  {
                                    tableDealTemplateListFields.map((tableField: TableField): ReactElement => (
                                      <TableCell key={tableField.id}>
                                        {
                                          !tableField.isHeaderNameHidden && (
                                            <TextBlock>
                                              {tableField.title}
                                            </TextBlock>
                                          )
                                        }
                                      </TableCell>
                                    ))
                                  }
                                </TableRow>
                              </TableHead>
                              <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
                                {
                                  dealTemplateListData.map((dealTemplate: DealTemplate): ReactElement => (
                                    <TableRow
                                      isButton
                                      key={dealTemplate.id}
                                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                        onRowClick(event, dealTemplate);
                                      }}
                                    >
                                      {
                                        tableDealTemplateListFields.map((
                                          tableField: TableField,
                                        ): ReactElement | boolean => (
                                          !!tableField.component && (
                                            <Fragment key={tableField.id}>
                                              {tableField.component(dealTemplate)}
                                            </Fragment>
                                          )
                                        ))
                                      }
                                    </TableRow>
                                  ))
                                }
                              </TableBody>
                            </Table>
                          </ContentListContainerOffset>
                        )
                      }
                      {
                        isSmallScreen && (
                          <ContentListContainerOffset isDefault withTabs>
                            <List>
                              {
                                dealTemplateListData.map((dealTemplate: DealTemplate): ReactElement => (
                                  <ListItem
                                    isButton
                                    isExtended
                                    key={dealTemplate.id}
                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                      onRowClick(event, dealTemplate);
                                    }}
                                  >
                                    <ListItemText isBlock>
                                      {dealTemplate.title}
                                    </ListItemText>
                                  </ListItem>
                                ))
                              }
                            </List>
                          </ContentListContainerOffset>
                        )
                      }
                    </Card>
                  ) : (
                    <NoContentBlock>
                      No deal templates found by your request
                    </NoContentBlock>
                  )
                }
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <SettingsDealTemplateModal
          isOpen={isDealTemplateModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          dealTemplateTypeList={dealTemplateTypeList}
          saveFormDeps={saveFormDeps}
          onCancel={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseModal={closeModal}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_DEAL_TEMPLATE_ID}
          onClose={closeActionMenu}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealTemplateDeleteConfirm}
          onCloseModal={closeDealTemplateDeleteConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    settings,
    settingsDealTemplateList,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  initialValues.dealTemplateListData = {
    [formKeyNew]: {
      title: null,
    },
  };

  return {
    isDataLoaded: !!settings.settingsData && !!settingsDealTemplateList.dealTemplateListData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    dealTemplateListData: settingsDealTemplateList && settingsDealTemplateList.dealTemplateListData
      && settingsDealTemplateList.dealTemplateListData.dealTemplateList,
    dealTemplateTypeList: settingsDealTemplateList && settingsDealTemplateList.dealTemplateListData
      && settingsDealTemplateList.dealTemplateListData.dealTemplateTypeList,
  };
};

const SettingsDealTemplateListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsDealTemplateList }: Reducers): ValidationErrorsMap => (
    settingsDealTemplateList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsDealTemplateList }: Reducers): ValidationErrorsMap => (
    settingsDealTemplateList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_DEAL_TEMPLATE_LIST}
      component={SettingsDealTemplateList}
      validator={SettingsDealTemplateListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsDealTemplateListForm);
